import { useCallback, useEffect, useMemo, useState } from "react";
import { useApp } from "./AppContext";
import {
  ApiError,
  type Contradiction,
  type OwnerQuestion,
  type PendingCorrection,
  type ReviewInsight,
  type UnnamedSpeaker,
} from "./api";

/**
 * UI-16 — everything that cannot be decided without a human, as one ordered stream.
 *
 * Five kinds, five reads, one queue. Nothing here is a new pipeline: each card is drawn
 * from a list some other screen already serves, and every answer goes back through the
 * call that screen already makes — approving an insight is INS-4's approve, choosing a
 * side is AUTO-1's resolve, applying a wording fix is SPCH-8's, naming a voice is SPCH-6's.
 * A card answered here is gone from the full-mode surface it also appears on, and the other
 * way round, because there is one row behind both.
 */
export type NeedsYouCard =
  | { kind: "correction"; id: string; waitingSince: string; correction: PendingCorrection }
  | { kind: "speaker"; id: string; waitingSince: string; speaker: UnnamedSpeaker }
  | { kind: "insight"; id: string; waitingSince: string; insight: ReviewInsight }
  | { kind: "contradiction"; id: string; waitingSince: string; contradiction: Contradiction }
  | { kind: "question"; id: string; waitingSince: string; question: OwnerQuestion };

export type NeedsYouKind = NeedsYouCard["kind"];

/**
 * **The ordering rule.** Cards come in the order the pipeline made them: a correction is
 * about what was *heard*, a speaker question about *who said it*, an insight about what was
 * *read out of it*, and a contradiction about what is *believed*.
 *
 * That is not a taste in sorting — it is the order in which an answer changes the questions
 * after it. Fixing a misheard name and naming the voice that said it both change the words
 * the insight below them was drawn from, and approving insights is what produces the
 * beliefs a contradiction is between. Answering down this list means never deciding
 * something on evidence you were about to correct.
 *
 * **A question is last** (UI-21, M17.3). The system asking the owner something is never blocking,
 * and every kind ahead of it changes the words and beliefs a question would be about — a voice
 * named, an insight approved or a disagreement settled can each fill the very gap the question
 * noticed, and the sweep then closes it without the owner ever being asked.
 *
 * Within a kind, oldest first: the thing that has been waiting longest goes first, which is
 * the only tiebreak that cannot be gamed by whatever the pipeline happened to finish last.
 */
const KIND_ORDER: NeedsYouKind[] = ["correction", "speaker", "insight", "contradiction", "question"];

export function orderQueue(cards: NeedsYouCard[]): NeedsYouCard[] {
  return [...cards].sort((left, right) => {
    const byKind = KIND_ORDER.indexOf(left.kind) - KIND_ORDER.indexOf(right.kind);
    if (byKind !== 0) return byKind;

    const byAge = left.waitingSince.localeCompare(right.waitingSince);
    if (byAge === 0 && left.kind === "question" && right.kind === "question") {
      // One sweep opens many questions at the same instant; the name keeps a person's two cards
      // together, and the kind puts "what do they do" before "which team".
      const byName = left.question.name.localeCompare(right.question.name);
      if (byName !== 0) return byName;
      const byShape = left.question.kind.localeCompare(right.question.kind);
      if (byShape !== 0) return byShape;
    }
    // The id is the last tiebreak so the order is total: two cards made in the same
    // transaction must not swap places between two renders of the same queue.
    return byAge !== 0 ? byAge : left.id.localeCompare(right.id);
  });
}

/**
 * What each kind is called and how its card opens, in the words UI-16's copy uses.
 *
 * Held here rather than on either screen, because both reduced surfaces draw the same
 * cards at different scales and the *words* are the feature: "Who is this?" rather than
 * "speaker identification", "A wording fix" rather than "correction proposal". Two copies
 * would be two chances for one surface to start speaking the system's language again.
 */
export const NEEDS_YOU_COPY: Record<NeedsYouKind, { label: string; lead: string; colour: string }> = {
  correction: { label: "Correction", lead: "A wording fix", colour: "var(--vl-bridge)" },
  speaker: { label: "Who is this?", lead: "A voice needs a name", colour: "var(--primary)" },
  insight: { label: "New insight", lead: "Something new was heard", colour: "var(--vl-virtual)" },
  contradiction: { label: "Contradiction", lead: "Two beliefs disagree", colour: "var(--warning)" },
  question: { label: "Tell me", lead: "Factorium has a question", colour: "var(--vl-life)" },
};

export interface NeedsYouQueue {
  /** Null until the first answer, which is how "loading" is told from "nothing waiting". */
  cards: NeedsYouCard[] | null;
  problem: string | null;
  reload: () => void;
  /** Drop one card the moment it is answered, and let the rail's badge catch up. */
  answered: (id: string) => void;
}

/**
 * The four reads behind the stream, and the local removal that keeps it shrinking.
 *
 * A hook rather than another field on the app context: unlike the rail's badge, which is on
 * screen everywhere, this is only ever wanted by whichever Needs-you screen is open, and
 * four requests on every cold load of every page would be four requests nobody asked for.
 */
export function useNeedsYou(): NeedsYouQueue {
  const { api, refreshReview } = useApp();

  const [cards, setCards] = useState<NeedsYouCard[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [corrections, speakers, insights, contradictions, questions] = await Promise.all([
        api.listCorrections(),
        api.listUnnamedSpeakers(),
        api.listReview(),
        api.listContradictions(),
        // The fifth read may fail on its own: a question is never blocking, so a queue that
        // could not read its questions still shows the four kinds that are.
        Promise.resolve()
          .then(() => api.listQuestions())
          .catch(() => []),
      ]);

      setCards(
        orderQueue([
          ...corrections.map((correction): NeedsYouCard => ({
            kind: "correction",
            id: correction.id,
            waitingSince: correction.createdAt,
            correction,
          })),
          ...speakers.map((speaker): NeedsYouCard => ({
            kind: "speaker",
            id: speaker.id,
            waitingSince: speaker.createdAt,
            speaker,
          })),
          ...insights.map((insight): NeedsYouCard => ({
            kind: "insight",
            id: insight.id,
            waitingSince: insight.createdAt,
            insight,
          })),
          ...contradictions.map((contradiction): NeedsYouCard => ({
            kind: "contradiction",
            id: contradiction.id,
            waitingSince: contradiction.createdAt,
            contradiction,
          })),
          ...questions.map((question): NeedsYouCard => ({
            kind: "question",
            id: question.id,
            waitingSince: question.createdAt,
            question,
          })),
        ]),
      );
      setProblem(null);
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "What is waiting could not be loaded.");
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const answered = useCallback(
    (id: string) => {
      setCards((current) => (current ? current.filter((card) => card.id !== id) : current));
      refreshReview();
    },
    [refreshReview],
  );

  return useMemo(
    () => ({ cards, problem, reload: () => void load(), answered }),
    [cards, problem, load, answered],
  );
}
